import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2, Inbox, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';

interface SiteMessage {
  id: number;
  text: string;
  text_en?: string;
  type: 'info' | 'warning' | 'error';
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminMessages = () => {
  const { t, i18n } = useTranslation();
  const [messages, setMessages] = useState<SiteMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingMessage, setEditingMessage] = useState<SiteMessage | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<SiteMessage | null>(null);
  const [formText, setFormText] = useState('');
  const [formTextEn, setFormTextEn] = useState('');
  const [formType, setFormType] = useState<SiteMessage['type']>('info');
  const [formIsActive, setFormIsActive] = useState(true);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/messages/all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Load failed');
      setMessages(data.data || []);
    } catch (error) {
      console.error('Error fetching site messages:', error);
      toast.error(t('admin.messagesPage.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const resetForm = () => {
    setFormText('');
    setFormTextEn('');
    setFormType('info');
    setFormIsActive(true);
    setEditingMessage(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (message: SiteMessage) => {
    setEditingMessage(message);
    setFormText(message.text);
    setFormTextEn(message.text_en || '');
    setFormType(message.type);
    setFormIsActive(message.is_active);
    setDialogOpen(true);
  };

  const saveMessage = async () => {
    if (!formText.trim()) {
      toast.error(t('admin.messagesPage.required'));
      return;
    }
    try {
      setSaving(true);
      const response = await fetch(editingMessage ? '/api/messages/update' : '/api/messages/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(editingMessage ? { id: editingMessage.id, is_active: formIsActive } : {}),
          text: formText.trim(),
          text_en: formTextEn.trim() || undefined,
          type: formType
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || 'Save failed');
      toast.success(editingMessage ? t('admin.messagesPage.updated') : t('admin.messagesPage.created'));
      setDialogOpen(false);
      resetForm();
      await fetchMessages();
    } catch (error) {
      console.error('Error saving site message:', error);
      toast.error(t('admin.messagesPage.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (message: SiteMessage) => {
    try {
      const response = await fetch('/api/messages/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: message.id, is_active: !message.is_active })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Update failed');
      setMessages((current) => current.map((entry) => entry.id === message.id ? { ...entry, is_active: !entry.is_active } : entry));
    } catch (error) {
      console.error('Error updating message status:', error);
      toast.error(t('admin.messagesPage.saveError'));
    }
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      setSaving(true);
      const response = await fetch('/api/messages/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: messageToDelete.id })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error('Delete failed');
      toast.success(t('admin.messagesPage.deleted'));
      setMessageToDelete(null);
      await fetchMessages();
    } catch (error) {
      console.error('Error deleting site message:', error);
      toast.error(t('admin.messagesPage.deleteError'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(
      i18n.language.toLowerCase().startsWith('sr') ? 'sr-RS' : 'en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' }
    );

  const typeLabel = (type: SiteMessage['type']) => t(`admin.messagesPage.types.${type}`);
  const activeCount = messages.filter((message) => message.is_active).length;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">{t('admin.messagesPage.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('admin.messagesPage.description')}</p>
      </div>

      <div className="mt-4">
          <section className="border-b border-border">
            <div className="flex flex-wrap items-center justify-between gap-3 py-4">
              <div>
                <h2 className="font-semibold">{t('admin.messagesPage.siteTitle')}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {t('admin.messagesPage.siteCount', { total: messages.length, active: activeCount })}
                </p>
              </div>
              <Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />{t('admin.messagesPage.new')}</Button>
            </div>

            <div className="overflow-x-auto py-3">
              {loading ? (
                <div className="space-y-2">{Array.from({ length: 4 }).map((_, index) => <Skeleton key={index} className="h-14 w-full" />)}</div>
              ) : messages.length === 0 ? (
                <div className="flex min-h-56 flex-col items-center justify-center text-center">
                  <Inbox className="mb-3 h-8 w-8 text-muted-foreground/60" />
                  <p className="font-medium">{t('admin.messagesPage.empty')}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{t('admin.messagesPage.emptyDescription')}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>{t('admin.messagesPage.columns.status')}</TableHead>
                    <TableHead className="w-[62%]">{t('admin.messagesPage.columns.message')}</TableHead>
                    <TableHead>{t('admin.messagesPage.columns.updated')}</TableHead>
                    <TableHead className="text-right">{t('admin.messagesPage.columns.actions')}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {messages.map((message) => (
                      <TableRow key={message.id}>
                        <TableCell className="py-4"><div className="flex items-center gap-2">
                          <Switch checked={message.is_active} onCheckedChange={() => toggleActive(message)} aria-label={t('admin.messagesPage.toggle')} />
                          <span className="text-sm text-muted-foreground">{message.is_active ? t('admin.messagesPage.active') : t('admin.messagesPage.inactive')}</span>
                        </div></TableCell>
                        <TableCell className="py-4"><p className="max-w-[760px] whitespace-pre-wrap text-sm">{message.text}</p><p className={`mt-2 border-l-2 pl-2 text-xs font-semibold ${message.type === 'info' ? 'border-sky-500 text-sky-700 dark:text-sky-300' : message.type === 'warning' ? 'border-amber-500 text-amber-800 dark:text-amber-300' : 'border-rose-500 text-rose-800 dark:text-rose-300'}`}>{typeLabel(message.type)}{message.text_en ? ` · ${t('admin.messagesPage.available')}` : ''}</p></TableCell>
                        <TableCell className="whitespace-nowrap py-4 text-sm text-muted-foreground">{formatDate(message.updatedAt)}</TableCell>
                        <TableCell className="py-4 text-right"><div className="inline-flex gap-2">
                          <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => openEdit(message)} aria-label={t('admin.messagesPage.edit')} title={t('admin.messagesPage.edit')}><Edit2 className="h-4 w-4" /></Button>
                          <Button size="icon" variant="destructive" className="h-8 w-8" onClick={() => setMessageToDelete(message)} aria-label={t('admin.messagesPage.delete')} title={t('admin.messagesPage.delete')}><Trash2 className="h-4 w-4" /></Button>
                        </div></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </section>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingMessage ? t('admin.messagesPage.editTitle') : t('admin.messagesPage.createTitle')}</DialogTitle>
            <DialogDescription>{t('admin.messagesPage.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label htmlFor="site-message-sr">{t('admin.messagesPage.serbian')}</Label><Textarea id="site-message-sr" value={formText} onChange={(event) => setFormText(event.target.value)} rows={4} /></div>
            <div className="space-y-2"><Label htmlFor="site-message-en">{t('admin.messagesPage.english')}</Label><Textarea id="site-message-en" value={formTextEn} onChange={(event) => setFormTextEn(event.target.value)} rows={4} /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label>{t('admin.messagesPage.columns.type')}</Label><Select value={formType} onValueChange={(value) => setFormType(value as SiteMessage['type'])}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="info">{t('admin.messagesPage.types.info')}</SelectItem><SelectItem value="warning">{t('admin.messagesPage.types.warning')}</SelectItem><SelectItem value="error">{t('admin.messagesPage.types.error')}</SelectItem></SelectContent></Select></div>
              {editingMessage ? <div className="space-y-2"><Label>{t('admin.messagesPage.columns.status')}</Label><div className="flex h-10 items-center gap-2"><Switch checked={formIsActive} onCheckedChange={setFormIsActive} /><span className="text-sm text-muted-foreground">{formIsActive ? t('admin.messagesPage.active') : t('admin.messagesPage.inactive')}</span></div></div> : null}
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>{t('admin.messagesPage.cancel')}</Button><Button onClick={saveMessage} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{t('admin.messagesPage.save')}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(messageToDelete)} onOpenChange={(open) => !open && setMessageToDelete(null)}>
        <DialogContent className="sm:max-w-md"><DialogHeader><DialogTitle>{t('admin.messagesPage.deleteTitle')}</DialogTitle><DialogDescription>{t('admin.messagesPage.deleteDescription')}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setMessageToDelete(null)} disabled={saving}>{t('admin.messagesPage.cancel')}</Button><Button variant="destructive" onClick={deleteMessage} disabled={saving}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}{t('admin.messagesPage.delete')}</Button></DialogFooter></DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMessages;
